// angular
import { Component, effect, inject, input, OnChanges } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
// components
import { AdditionalSpecificationBoxComponent } from '@app/core/components/additional-specification-box/additional-specification-box.component';
import { ButtonComponent } from '@app/core/components/button/button.component';
// interfaces & types
import { AdditionalSpecificationType } from '@app/+products/shared/interfaces/additional-specification.interface';
import { AdditionalProperty } from '@app/+products/shared/types/additional-property.type';
// pipes
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';

@Component({
  selector: 'pwj-product-additional-properties',
  standalone: true,
  imports: [
    AdditionalSpecificationBoxComponent,
    TranslateModule,
    TranslateDataPipe,
    ButtonComponent,
  ],
  templateUrl: './product-additional-properties.component.html',
  styleUrl: './product-additional-properties.component.scss',
})
export class ProductAdditionalPropertiesComponent {
  public properties!: AdditionalProperty[];
  public showAdditionalProperties: boolean = true;

  // inputs
  additionalProperties = input.required<AdditionalSpecificationType>();

  // dependencies
  private readonly _translateService = inject(TranslateService);

  constructor() {
    effect(() => {
      this.setProperties();
    });

    // Update properties on lang change
    this._translateService.onLangChange.subscribe(_ => {
      this.setProperties();
    });
  }

  public setProperties() {
    // set additionalProperties data

    this.properties = [
      {
        title: this._translateService.instant('products.warranty'),
        value: this.additionalProperties()?.warranty
          ? `${
              this.additionalProperties()?.warranty
            } ${this._translateService.instant('products.month')}`
          : '-',
      },
      {
        title: 'products.monthlyProduction',
        value: this.additionalProperties()?.production_capacity?.value
          ? `${
              this.additionalProperties()?.production_capacity?.value
            } ${this._translateService.instant(
              `enums.measurements.${
                this.additionalProperties()?.production_capacity?.unit
              }`
            )}`
          : '-',
      },
      // colors properties
      {
        title: 'products.mainColor',
        colorsList: [
          {
            name: this.additionalProperties()?.main_color
              ? this._translateService.instant(
                  `enums.colors.${this.additionalProperties()?.main_color}`
                )
              : '-',
            color: this.additionalProperties()?.main_color,
          },
        ],
        colorPercentage: this.additionalProperties()?.color_percentage,
      },
      {
        title: 'products.vein',
        colorsList: this.additionalProperties()?.veins?.map(
          (value: string) => ({
            name: value
              ? this._translateService.instant(`enums.colors.${value}`)
              : '-',
            color: value,
          })
        ),
      },
      {
        title: 'products.point',
        colorsList: this.additionalProperties()?.dots?.map((value: string) => ({
          name: value
            ? this._translateService.instant(`enums.colors.${value}`)
            : '-',
          color: value,
        })),
      },
      {
        title: 'products.halo',
        colorsList: this.additionalProperties()?.halos?.map(
          (value: string) => ({
            name: value
              ? this._translateService.instant(`enums.colors.${value}`)
              : '-',
            color: value,
          })
        ),
      },
      {
        title: 'products.theNumberOfCuttingFaces',
        value: this.additionalProperties()?.cutting_face,
      },
      {
        title: 'products.minOrder',
        value: this.additionalProperties()?.min_order?.value
          ? `${
              this.additionalProperties()?.min_order?.value
            } ${this._translateService.instant(
              `enums.measurements.${
                this.additionalProperties()?.min_order?.unit
              }`
            )}`
          : '-',
      },
      {
        title: 'products.shipmentTerms',
        value:
          (this.additionalProperties()?.shipment_terms?.length || 0) > 0
            ? this.additionalProperties()?.shipment_terms?.join(' , ')
            : '-',
      },
      {
        title: 'products.paymentMethods',
        value:
          (this.additionalProperties()?.payment_methods?.length || 0) > 0
            ? this.additionalProperties()?.payment_methods?.join(' , ')
            : '-',
      },
      {
        title: 'products.payment',
        value:
          this.additionalProperties()?.payment_deposit &&
          this.additionalProperties()?.payment_balance
            ? `${
                this.additionalProperties()?.payment_deposit
              }%  ${this._translateService.instant(
                'products.depositBeforeProduction'
              )}  ${
                this.additionalProperties()?.payment_balance
              }%  ${this._translateService.instant(
                'products.balanceBeforeShipping'
              )}`
            : null,
      },
      {
        title: 'products.availableAmount',
        value: this.additionalProperties()?.available_amount?.value
          ? `${
              this.additionalProperties()?.available_amount?.value
            } ${this._translateService.instant(
              `enums.measurements.${
                this.additionalProperties()?.available_amount?.unit
              }`
            )}`
          : '-',
      },
      {
        title: 'products.address',
        value: this.additionalProperties()?.delivery_address,
      },
      {
        title: 'products.deliveryType',
        value: this.additionalProperties()?.delivery_type
          ? this._translateService.instant(
              `enums.deliveryType.${this.additionalProperties()?.delivery_type}`
            )
          : '-',
      },
      {
        title: 'products.size',
        titleSuffix: this.additionalProperties()?.customizeSize
          ? 'products.custom'
          : null,
        sizeList: this.additionalProperties()?.dimensions?.map(size => ({
          length: {
            title: 'products.length',
            unit: size?.length?.unit || '-',
            value: size?.length?.value || '-',
          },
          length_tolerance: {
            title: 'products.toleranceLength',
            unit: size?.length_tolerance?.unit || '-',
            value: size?.length_tolerance?.value || '-',
          },
          width: {
            title: 'products.width',
            unit: size?.width?.unit || '-',
            value: size?.width?.value || '-',
          },
          width_tolerance: {
            title: 'products.toleranceWidth',
            unit: size?.width_tolerance?.unit || '-',
            value: size?.width_tolerance?.value || '-',
          },
          height: {
            title: 'products.height',
            unit: size?.height?.unit || '-',
            value: size?.height?.value || '-',
          },
          height_tolerance: {
            title: 'products.toleranceHeight',
            unit: size?.height_tolerance?.unit || '-',
            value: size?.height_tolerance?.value || '-',
          },
          weight: {
            title: 'products.weight',
            unit: size?.weight?.unit || '-',
            value: size?.weight?.value || '-',
          },
          weight_tolerance: {
            title: 'products.toleranceWeight',
            unit: size?.weight_tolerance?.unit || '-',
            value: size?.weight_tolerance?.value || '-',
          },
          thickness: {
            title: 'products.thickness',
            unit: size?.thickness?.unit || '-',
            value: size?.thickness?.value || '-',
          },
        })),
        isEmpty:
          !this.additionalProperties()?.dimensions ||
          (this.additionalProperties()?.dimensions?.length || 0) < 1,
      },
    ];
  }

  public setColorBoxStyle(color?: string) {
    return `background: ${color};`;
  }

  public toggleAdditionalProperties() {
    this.showAdditionalProperties = !this.showAdditionalProperties;
  }
}
